import { Box } from "@mui/material";
import BuildNewProductCard from "./BuildNewProductCard";
import DraftProductCard from "./DraftProductCard";
import PendingCreationCard from "./PendingCreationCard";
import ProductCard from "./ProductCard";
import type { Product } from "./types";
import type { ProductCreationDraftRecord } from "../newProduct/draftApi";
import type { ProductCreationOperation } from "../newProduct/productCreationOperationsApi";
import type { TrackedBatch } from "../batchGeneration";

interface ProductsGridViewProps {
    products: Product[];
    onOpenProduct?: (product: Product) => void;
    onEditProduct?: (product: Product) => void;
    onDeleteProduct?: (product: Product) => void;
    onSyncProduct?: (product: Product) => void;
    onGenerateAll?: (product: Product) => void;
    onAnalyzeContext?: (product: Product) => void;
    onRetryBuild?: (product: Product) => void;
    retryingProductIds?: ReadonlySet<string>;
    retryErrorsByProductId?: ReadonlyMap<string, string>;
    onOpenBatchDetails?: () => void;
    batchesByProductId?: ReadonlyMap<string, TrackedBatch>;
    pendingBatchProductIds?: ReadonlySet<string>;
    onCreateProduct?: () => void;
    createCardTitle?: string;
    createCardDescription?: string;
    creationOperations?: ProductCreationOperation[];
    onRetryCreation?: (operation: ProductCreationOperation) => void;
    onReturnCreationToDraft?: (operation: ProductCreationOperation) => void;
    retryingCreationIds?: ReadonlySet<string>;
    returningCreationIds?: ReadonlySet<string>;
    drafts?: ProductCreationDraftRecord[];
    onResumeDraft?: (draft: ProductCreationDraftRecord) => void;
    onDeleteDraft?: (draft: ProductCreationDraftRecord) => void;
}

const ProductsGridView = ({
    products,
    onOpenProduct,
    onEditProduct,
    onDeleteProduct,
    onSyncProduct,
    onGenerateAll,
    onAnalyzeContext,
    onRetryBuild,
    retryingProductIds,
    retryErrorsByProductId,
    onOpenBatchDetails,
    batchesByProductId,
    pendingBatchProductIds,
    onCreateProduct,
    createCardTitle,
    createCardDescription,
    creationOperations = [],
    onRetryCreation,
    onReturnCreationToDraft,
    retryingCreationIds,
    returningCreationIds,
    drafts = [],
    onResumeDraft,
    onDeleteDraft
}: ProductsGridViewProps) => (
    <Box
        aria-label="Product cards"
        sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 1.75
        }}
    >
        {onCreateProduct && <BuildNewProductCard onClick={onCreateProduct} title={createCardTitle} description={createCardDescription} />}
        {onResumeDraft &&
            onDeleteDraft &&
            drafts.map(draft => <DraftProductCard key={draft.draft_id} draft={draft} onResume={onResumeDraft} onDelete={onDeleteDraft} />)}
        {onRetryCreation &&
            onReturnCreationToDraft &&
            creationOperations.map(operation => (
                <PendingCreationCard
                    key={operation.id}
                    operation={operation}
                    onRetry={onRetryCreation}
                    onReturnToDraft={onReturnCreationToDraft}
                    retrying={retryingCreationIds?.has(operation.id)}
                    returningToDraft={returningCreationIds?.has(operation.id)}
                />
            ))}
        {products.map(product => (
            <ProductCard
                key={product.id}
                product={product}
                onOpen={onOpenProduct}
                onEdit={onEditProduct}
                onDelete={onDeleteProduct}
                onSync={onSyncProduct}
                onGenerateAll={onGenerateAll}
                onAnalyzeContext={onAnalyzeContext}
                onRetryBuild={onRetryBuild}
                retryPending={retryingProductIds?.has(product.id)}
                retryError={retryErrorsByProductId?.get(product.id)}
                onOpenBatchDetails={onOpenBatchDetails}
                batch={batchesByProductId?.get(product.id)}
                batchPending={pendingBatchProductIds?.has(product.id)}
            />
        ))}
    </Box>
);

export default ProductsGridView;
